/**
 * Centralized z-index management for the component library
 *
 * This system ensures proper layering hierarchy across all components
 * and prevents z-index conflicts.
 */

export const Z_INDEX = {
  // Base layer - content elements
  base: 1,

  // Interactive elements that need to appear above base content
  raised: 10,

  // Navigation and layout elements
  sidebar: 30,
  header: 35,

  // Table sticky headers and similar layout-bound elements
  tableHeader: 40,

  // Dropdowns and floating elements
  dropdown: 1000,
  popover: 1020,

  // Overlays and modals
  overlay: 2000,
  sheet: 2020,
  modalOverlay: 2030,
  modal: 2040,
  alertDialog: 2060,

  // Floating windows (above overlays)
  floatingWindow: 2500,

  // Tooltips (above floating windows since they provide context for hovered elements)
  tooltip: 2600,

  // Toast notifications (highest priority)
  toast: 3000,
} as const;

export type ZIndexLayer = keyof typeof Z_INDEX;

/**
 * Get z-index value for a given layer
 */
export function getZIndex(layer: ZIndexLayer): number {
  return Z_INDEX[layer];
}

/**
 * Generate Tailwind CSS z-index class name for a layer
 */
export function getZIndexClass(layer: ZIndexLayer): string {
  const value = Z_INDEX[layer];
  return `z-[${value}]`;
}

/**
 * Tailwind CSS z-index utilities mapped to our system
 */
export const Z_INDEX_CLASSES = {
  base: getZIndexClass("base"),
  raised: getZIndexClass("raised"),
  sidebar: getZIndexClass("sidebar"),
  header: getZIndexClass("header"),
  tableHeader: getZIndexClass("tableHeader"),
  dropdown: getZIndexClass("dropdown"),
  tooltip: getZIndexClass("tooltip"),
  popover: getZIndexClass("popover"),
  overlay: getZIndexClass("overlay"),
  modal: getZIndexClass("modal"),
  sheet: getZIndexClass("sheet"),
  alertDialog: getZIndexClass("alertDialog"),
  floatingWindow: getZIndexClass("floatingWindow"),
  toast: getZIndexClass("toast"),
} as const;
